import { useState, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { useTextColorClass } from "@/components/ui/text-coloring";
import { useIsMobile } from "@/hooks/use-mobile";
import { Play, FileText, Clock, Loader2 } from "lucide-react";
import VideoPlayer from "./VideoPlayer";
import DocumentModal from "./DocumentModal";
import { supportLabels } from "@/i18n";
import { useLanguage } from "@/context/LanguageContext";
import { useSupportGuidesPublic } from "@/hooks/useSupportGuides";
import { normalizeSupportGuideVideoUrl } from "@/config/constants";
import type { SupportGuideDto } from "@/types/supportGuide";

interface SupportGuideItem {
  id: string;
  title: string;
  description: string;
  duration: string;
  type: "video" | "document";
  videoUrl?: string;
}

/** Stable empty array to avoid new reference on each render (prevents useEffect loop) */
const EMPTY_VIDEO_GUIDES: SupportGuideDto[] = [];

/** Document guide IDs (content comes from i18n) */
const DOCUMENT_GUIDE_IDS = [
  "howAuctionsWork",
  "paymentDelivery",
  "beforeBidAndPurchase",
  "deliveryChecklist",
  "ifSomethingWrong",
] as const;

function getDocumentGuides(labels: Record<string, any>): SupportGuideItem[] {
  const dg = labels?.guides?.documentGuides;
  const durationMinutes = labels?.guides?.durationMinutes ?? "ca {n} min";
  const durationMinute = labels?.guides?.durationMinute ?? "ca 1 min";
  if (!dg) return [];

  return DOCUMENT_GUIDE_IDS
    .filter((id) => dg[id])
    .map((id) => {
      const g = dg[id];
      const n = g.duration || "1";
      const duration =
        n === "1"
          ? durationMinute
          : (durationMinutes || "ca {n} min").replace("{n}", n);
      return {
        id: id as string,
        title: g.title || id,
        description: g.description || "",
        duration,
        type: "document" as const,
      };
    });
}

export const SupportGuides = () => {
  const [selectedGuide, setSelectedGuide] = useState<SupportGuideItem | null>(
    null
  );
  const [showVideoPlayer, setShowVideoPlayer] = useState(false);
  const { baseText, subtleText, isDark } = useTextColorClass();
  const isMobile = useIsMobile();
  const { language } = useLanguage();
  const supportLabels_i18n = supportLabels(language);

  const { data: apiVideoGuides = EMPTY_VIDEO_GUIDES, isLoading: isLoadingVideoGuides } =
    useSupportGuidesPublic({ type: "video" });

  const supportGuides = useMemo<SupportGuideItem[]>(() => {
    const durationVideo = supportLabels_i18n?.guides?.durationVideo ?? "Video";
    const videoItems: SupportGuideItem[] = apiVideoGuides
      .filter((g) => g.videoUrl && g.isPublished)
      .map((g) => {
        const duration =
          g.videoDuration != null
            ? g.videoDuration >= 60
              ? `${Math.floor(g.videoDuration / 60)} min`
              : `${Math.floor(g.videoDuration)} sek`
            : durationVideo;
        return {
          id: g.id,
          title: g.title,
          description: g.description,
          duration,
          type: "video" as const,
          videoUrl: normalizeSupportGuideVideoUrl(g.videoUrl) ?? g.videoUrl,
        };
      });
    const docGuides = getDocumentGuides(supportLabels_i18n);
    return [...videoItems, ...docGuides];
  }, [apiVideoGuides, supportLabels_i18n]);

  const handleGuideClick = (guide: SupportGuideItem) => {
    setSelectedGuide(guide);
    if (guide.type === "video") {
      setShowVideoPlayer(true);
    }
  };

  const closeModal = () => {
    setSelectedGuide(null);
    setShowVideoPlayer(false);
  };

  return (
    <div className="glass-card rounded-xl border border-border/60 p-4 sm:p-6 my-6 sm:my-8">
      <div className="flex items-center gap-2 mb-4">
        <h2 className="text-lg sm:text-xl font-semibold">
          {supportLabels_i18n.guides?.title}
        </h2>
      </div>

      {isLoadingVideoGuides ? (
        <div className="flex items-center justify-center py-12 rounded-lg border border-border/60 bg-muted/20">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {supportGuides.map((guide, index) => (
          <Card
            key={index}
            className="rounded-lg cursor-pointer hover-scale border-border/60 bg-background/80"
            onClick={() => handleGuideClick(guide)}
          >
            <CardContent className="p-4 sm:p-6">
              <div className="flex flex-col space-y-2">
                <div className="flex justify-between items-start gap-2">
                  <h3
                    className={`text-base sm:text-lg font-semibold ${baseText} break-words flex-1 min-w-0`}
                  >
                    {guide.title}
                  </h3>
                  {guide.type === "video" ? (
                    <div className="rounded-full bg-[#D4AF37]/20 p-1.5 flex-shrink-0">
                      <Play className="h-3 w-3 text-[#D4AF37]" />
                    </div>
                  ) : (
                    <div className="rounded-full bg-blue-500/20 p-1.5 flex-shrink-0">
                      <FileText className="h-3 w-3 text-blue-500" />
                    </div>
                  )}
                </div>

                <p className={`text-xs sm:text-sm ${subtleText}`}>
                  {guide.description}
                </p>

                <div className="flex items-center pt-2">
                  <Clock className={`h-3 w-3 ${subtleText} mr-1.5`} />
                  <span className={`text-xs ${subtleText}`}>
                    {guide.duration}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      )}

      {showVideoPlayer && selectedGuide && selectedGuide.videoUrl && (
        <VideoPlayer
          videoUrl={selectedGuide.videoUrl}
          title={selectedGuide.title}
          onClose={closeModal}
        />
      )}

      {selectedGuide &&
        selectedGuide.type === "document" &&
        !showVideoPlayer && (
          <DocumentModal
            guideId={selectedGuide.id}
            guide={selectedGuide}
            onClose={closeModal}
          />
        )}
    </div>
  );
};
